/**
 * CreateGroupModal Component
 *
 * Modal for creating new group conversations.
 * Features multi-select user picker, group name input, and avatar upload.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  Check,
  Loader2,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConnections } from '@/stores/connection';
import { useMessagingActions } from '@/stores/messaging';
import { SUCCESS_GREEN } from '@/styles/tokens';
import type { UserConnection } from '@/types/connections';

interface CreateGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_GROUP_SIZE = 50;

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * SelectableConnectionCard - User card with checkbox for multi-select
 */
interface SelectableConnectionCardProps {
  connection: UserConnection;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const SelectableConnectionCard: React.FC<SelectableConnectionCardProps> = ({
  connection,
  isSelected,
  onToggle,
  disabled,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`w-full p-3 rounded-lg border transition-all duration-200 text-left ${
        isSelected
          ? 'border-primary bg-primary/5'
          : disabled
          ? 'border-border bg-muted/50 opacity-60 cursor-not-allowed'
          : 'border-border bg-card hover:border-border hover:bg-muted/50 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarImage src={connection.user.avatar} alt={displayName} />
            <AvatarFallback
              className="border border-primary/20 bg-primary/10 text-xs font-semibold text-primary"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {connection.user.isOnline && (
            <span
              className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
              style={{ backgroundColor: SUCCESS_GREEN.DEFAULT }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
        </div>

        <div
          className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'border-primary bg-primary'
              : 'border-border bg-card'
          }`}
        >
          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
        </div>
      </div>
    </button>
  );
};

/**
 * SelectedMemberChip - Small chip showing selected member
 */
interface SelectedMemberChipProps {
  connection: UserConnection;
  onRemove: () => void;
}

const SelectedMemberChip: React.FC<SelectedMemberChipProps> = ({
  connection,
  onRemove,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-border bg-muted/50">
      <Avatar className="h-5 w-5">
        <AvatarImage src={connection.user.avatar} alt={displayName} />
        <AvatarFallback
          className="border border-primary/20 bg-primary/10 text-[10px] font-medium text-primary"
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <span className="text-xs font-medium text-foreground max-w-[80px] truncate">
        {displayName}
      </span>
      <button
        onClick={onRemove}
        className="text-muted-foreground hover:text-foreground transition-colors"
        aria-label={`Remove ${displayName}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const connections = useConnections();
  const { createGroup } = useMessagingActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedConnections, setSelectedConnections] = useState<UserConnection[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter connections based on search
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return connections;

    const query = searchQuery.toLowerCase().trim();
    return connections.filter((c) => {
      const name = `${c.user.firstName} ${c.user.lastName}`.toLowerCase();
      const username = c.user.username.toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [connections, searchQuery]);

  // Selected member IDs for quick lookup
  const selectedMemberIds = useMemo(
    () => new Set(selectedConnections.map((c) => c.user.id)),
    [selectedConnections]
  );

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setGroupName('');
      setSelectedConnections([]);
      setError(null);
    }
  }, [open]);

  /**
   * Toggle connection selection
   */
  const handleToggleConnection = useCallback((connection: UserConnection) => {
    setSelectedConnections((prev) => {
      const isSelected = prev.some((c) => c.user.id === connection.user.id);
      if (isSelected) {
        return prev.filter((c) => c.user.id !== connection.user.id);
      }
      // Check max size (minus 1 for the creator)
      if (prev.length >= MAX_GROUP_SIZE - 1) {
        return prev;
      }
      return [...prev, connection];
    });
    setError(null);
  }, []);

  /**
   * Remove a selected connection
   */
  const handleRemoveConnection = useCallback((userId: string) => {
    setSelectedConnections((prev) =>
      prev.filter((c) => c.user.id !== userId)
    );
    setError(null);
  }, []);

  /**
   * Handle creating the group
   */
  const handleCreateGroup = useCallback(async () => {
    if (!groupName.trim() || selectedConnections.length === 0) return;

    setIsCreating(true);
    setError(null);

    try {
      const memberIds = selectedConnections.map((c) => c.user.id);
      const conversation = await createGroup(groupName.trim(), memberIds);

      if (conversation) {
        onOpenChange(false);
        navigate(`/dashboard/messages?conversation=${conversation.id}`);
      } else {
        setError('Failed to create group. Please try again.');
      }
    } catch (err) {
      console.error('[CreateGroupModal] Failed to create group:', err);
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  }, [groupName, selectedConnections, createGroup, onOpenChange, navigate]);

  const canCreate = groupName.trim().length > 0 && selectedConnections.length > 0 && !isCreating;
  const isAtMaxCapacity = selectedConnections.length >= MAX_GROUP_SIZE - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-card-foreground">
            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
            Create Group
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1 ml-12">
            Add members from your connections to create a group chat
          </DialogDescription>
        </DialogHeader>

        {/* Group name input */}
        <div className="px-6 pb-4">
          <label className="text-sm font-medium text-foreground mb-2 block">
            Group Name <span className="text-destructive">*</span>
          </label>
          <Input
            placeholder="Enter group name..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            maxLength={100}
          />
          <span className="text-xs text-muted-foreground mt-1 block">{groupName.length}/100</span>
        </div>

        {/* Selected members preview */}
        {selectedConnections.length > 0 && (
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">
                Selected Members ({selectedConnections.length})
              </label>
              {isAtMaxCapacity && (
                <span className="text-xs text-amber-500">Max capacity reached</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedConnections.map((connection) => (
                <SelectedMemberChip
                  key={connection.user.id}
                  connection={connection}
                  onRemove={() => handleRemoveConnection(connection.user.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Search input */}
        <div className="px-6 pb-4">
          <label className="text-sm font-medium text-foreground mb-2 block">
            Add Members
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Connections list */}
        <ScrollArea className="flex-1 min-h-[150px] max-h-[200px]">
          <div className="px-6 pb-4 space-y-2">
            {filteredConnections.length === 0 ? (
              <div className="text-center py-6">
                <div className="h-10 w-10 rounded-full mx-auto mb-2 flex items-center justify-center bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium mb-1 text-foreground">
                  {searchQuery ? 'No connections found' : 'No connections yet'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Connect with users to add them to groups'}
                </p>
              </div>
            ) : (
              filteredConnections.map((connection) => {
                const isSelected = selectedMemberIds.has(connection.user.id);
                const isDisabled = !isSelected && isAtMaxCapacity;
                return (
                  <SelectableConnectionCard
                    key={connection.connectionId}
                    connection={connection}
                    isSelected={isSelected}
                    onToggle={() => handleToggleConnection(connection)}
                    disabled={isDisabled}
                  />
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Error message */}
        {error && (
          <div className="px-6 pb-2">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="justify-between">
          <span className="text-xs text-muted-foreground">
            {selectedConnections.length + 1} of {MAX_GROUP_SIZE} members (including you)
          </span>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={!canCreate}
              className={canCreate ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Create Group
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGroupModal;
